import { centsToDollars, parsePrice } from "helpers/price";
import { HorizontalLine } from "pages/Dashboard/components/styled";
import { Dropdown } from "primereact/dropdown";
import { TabPanel, TabView } from "primereact/tabview";
import { useState, useMemo, useEffect } from "react";
import styled from "styled-components";

import { optionsToCompareRates, comparisonRateCode } from "../helpers";
import { AIRecommendations } from "./AIRecommendations";
import { AvgPriceSpread } from "./AvgPriceSpread";
import { LowScoringPayers } from "./LowScoringPayers";
import { Metrics } from "./Metrics";
import { TopScoringPayers } from "./TopScoringPayers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledCompareRateTag = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  /* min-width: 250px; */
  width: 30%;
  min-width: 300px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 30% !important;
  min-width: 300px;
  @media (max-width: 768px) {
    width: 100% !important;
  }
  .p-dropdown-label {
    color: var(--primary-color);
    font-size: 14px;
  }
  .p-dropdown-trigger-icon {
    color: var(--primary-color);
  }

  .p-dropdwon {
    border-color: #d0d5dd;
  }
`;

const StyledTabView = styled(TabView)`
  .p-tabview-nav li.p-highlight .p-tabview-nav-link {
    background: #edf1fd !important;
  }
  .p-tabview-panels {
    background-color: white !important;
  }
`;

export function Insights({
  top10Data,
  topResultsAmount,
  dataFromAPIAllowableVsPtResponsibility,
  hasMarketPriceData,
  contractInsightsData,
  isLoadingContractInsights,
}: {
  top10Data: any;
  topResultsAmount: number;
  dataFromAPIAllowableVsPtResponsibility: any;
  hasMarketPriceData?: boolean;
  contractInsightsData?: any;
  isLoadingContractInsights?: boolean;
}) {
  // Filter options based on whether market price data exists
  const filteredOptionsToCompareRates = useMemo(() => {
    if (hasMarketPriceData) {
      return optionsToCompareRates;
    }
    // Remove market average option if no market price data
    return optionsToCompareRates.filter(
      (option) => option.value !== "marketAverage"
    );
  }, [hasMarketPriceData]);

  const [leftSideToCompare, setLeftSideToCompare] = useState(
    filteredOptionsToCompareRates[0]?.value || optionsToCompareRates[0].value
  );
  const [rightSideToCompare, setRightSideToCompare] = useState(
    filteredOptionsToCompareRates[1]?.value || optionsToCompareRates[1].value
  );
  const [activeTab, setActiveTab] = useState(0);
  const [showAllLeftSidePayers, setShowAllLeftSidePayers] = useState(false);
  const [showAllRightSidePayers, setShowAllRightSidePayers] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [rateToShow, setRateToShow] = useState(
    top10Data?.payerCentricApproachSummary?.variance?.highestVsLowestCommercial
  );

  // Reset selections if they become invalid due to market data availability change
  useEffect(() => {
    const isLeftValid = filteredOptionsToCompareRates.some(
      (opt) => opt.value === leftSideToCompare
    );
    const isRightValid = filteredOptionsToCompareRates.some(
      (opt) => opt.value === rightSideToCompare
    );

    if (!isLeftValid && filteredOptionsToCompareRates.length > 0) {
      setLeftSideToCompare(filteredOptionsToCompareRates[0].value);
    }
    if (!isRightValid && filteredOptionsToCompareRates.length > 1) {
      setRightSideToCompare(filteredOptionsToCompareRates[1].value);
    }
  }, [filteredOptionsToCompareRates, leftSideToCompare, rightSideToCompare]);

  useEffect(() => {
    if (!top10Data || !top10Data?.enriched.length) return;
    if (leftSideToCompare === rightSideToCompare) {
      setRateToShow(null);
      return;
    }
    const comparison = comparisonRateCode.find(
      (c) =>
        c.leftSide === leftSideToCompare && c.rightSide === rightSideToCompare
    );
    if (comparison) {
      setRateToShow(
        top10Data.payerCentricApproachSummary.variance[comparison.rateToShow]
      );
    } else {
      setRateToShow(null);
    }
  }, [top10Data, leftSideToCompare, rightSideToCompare]);

  const highestCommercialPayers = useMemo(() => {
    if (!top10Data || !top10Data?.enriched.length) return null;
    return top10Data.payerCentricApproachSummary.highestCommercialPayers.map(
      (p: any) => p.payerName
    );
  }, [top10Data]);

  const lowestCommercialPayers = useMemo(() => {
    if (!top10Data || !top10Data?.enriched.length) return null;
    return top10Data.payerCentricApproachSummary.lowestCommercialPayers.map(
      (p: any) => p.payerName
    );
  }, [top10Data]);

  const leftSideData = useMemo(() => {
    if (!top10Data || !top10Data?.enriched.length) return null;
    const option = filteredOptionsToCompareRates.find(
      (opt) => opt.value === leftSideToCompare
    );
    if (!option) return null;
    const rate =
      top10Data.payerCentricApproachSummary[
        option.value as keyof typeof top10Data.payerCentricApproachSummary
      ];
    const { icon, label } = option;
    let payers = [];
    if (leftSideToCompare === "maxCommercialAverage") {
      payers = highestCommercialPayers;
    } else if (leftSideToCompare === "minCommercialAverage") {
      payers = lowestCommercialPayers;
    }

    // Check if we need to show coverage info
    const { marketCoverageInfo } = top10Data.payerCentricApproachSummary;
    const showCoverage =
      marketCoverageInfo &&
      (leftSideToCompare === "marketAverage" ||
        rightSideToCompare === "marketAverage");

    return {
      icon: icon as React.ElementType,
      label,
      rate: parsePrice(centsToDollars(rate)),
      payers,
      showCoverage,
      coverageText: showCoverage
        ? `(${marketCoverageInfo.matchedCptCodes} of ${marketCoverageInfo.totalCptCodes} CPT codes)`
        : undefined,
    };
  }, [
    leftSideToCompare,
    rightSideToCompare,
    top10Data,
    highestCommercialPayers,
    lowestCommercialPayers,
    filteredOptionsToCompareRates,
  ]);

  const rightSideData = useMemo(() => {
    if (!top10Data || !top10Data?.enriched.length) return null;
    const option = filteredOptionsToCompareRates.find(
      (opt) => opt.value === rightSideToCompare
    );
    if (!option) return null;
    const rate =
      top10Data.payerCentricApproachSummary[
        option.value as keyof typeof top10Data.payerCentricApproachSummary
      ];
    const { icon, label } = option;
    let payers = [];
    if (rightSideToCompare === "maxCommercialAverage") {
      payers = highestCommercialPayers;
    } else if (rightSideToCompare === "minCommercialAverage") {
      payers = lowestCommercialPayers;
    }

    // Check if we need to show coverage info
    const { marketCoverageInfo } = top10Data.payerCentricApproachSummary;
    const showCoverage =
      marketCoverageInfo &&
      (leftSideToCompare === "marketAverage" ||
        rightSideToCompare === "marketAverage");

    return {
      icon: icon as React.ElementType,
      label,
      rate: parsePrice(centsToDollars(rate)),
      payers,
      showCoverage,
      coverageText: showCoverage
        ? `(${marketCoverageInfo.matchedCptCodes} of ${marketCoverageInfo.totalCptCodes} CPT codes)`
        : undefined,
    };
  }, [
    leftSideToCompare,
    rightSideToCompare,
    top10Data,
    highestCommercialPayers,
    lowestCommercialPayers,
    filteredOptionsToCompareRates,
  ]);

  const summary = useMemo(() => {
    return [
      {
        label: "Highest Avg Commercial Rate",
        value: top10Data?.payerCentricApproachSummary.maxCommercialAverage
          ? parsePrice(
              centsToDollars(
                top10Data.payerCentricApproachSummary.maxCommercialAverage
              )
            )
          : "N/A",
        payers:
          top10Data?.payerCentricApproachSummary.highestCommercialPayers.map(
            (p: any) => p.payerName
          ),
        key: "highestCommercialPayers",
      },
      {
        label: "Lowest Avg Commercial Rate",
        value: top10Data?.payerCentricApproachSummary.minCommercialAverage
          ? parsePrice(
              centsToDollars(
                top10Data.payerCentricApproachSummary.minCommercialAverage
              )
            )
          : "N/A",
        payers:
          top10Data?.payerCentricApproachSummary.lowestCommercialPayers.map(
            (p: any) => p.payerName
          ),
        key: "lowestCommercialPayers",
      },
      {
        label: "Medicare Avg Rate",
        value: top10Data?.payerCentricApproachSummary.medicareAverage
          ? parsePrice(
              centsToDollars(
                top10Data.payerCentricApproachSummary.medicareAverage
              )
            )
          : "N/A",
      },
      {
        label: "Avg Commercial Rate",
        value: top10Data?.payerCentricApproachSummary
          .averagePriceOfferedAccrossPayers
          ? parsePrice(
              centsToDollars(
                top10Data.payerCentricApproachSummary
                  .averagePriceOfferedAccrossPayers
              )
            )
          : "N/A",
      },
    ];
  }, [top10Data]);

  const leftSideVisiblePayers = useMemo(() => {
    if (!leftSideData?.payers) return [];
    return showAllLeftSidePayers
      ? leftSideData.payers
      : leftSideData.payers.slice(0, 3);
  }, [leftSideData, showAllLeftSidePayers]);

  const leftSideRemainingPayersCount = useMemo(() => {
    if (!leftSideData?.payers) return 0;
    return leftSideData.payers.length - 3;
  }, [leftSideData]);

  const rightSideVisiblePayers = useMemo(() => {
    if (!rightSideData?.payers) return [];
    return showAllRightSidePayers
      ? rightSideData.payers
      : rightSideData.payers.slice(0, 3);
  }, [rightSideData, showAllRightSidePayers]);

  const rightSideRemainingPayersCount = useMemo(() => {
    if (!rightSideData?.payers) return 0;
    return rightSideData.payers.length - 3;
  }, [rightSideData]);

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleTabChange = (e: any) => {
    console.log("e", e);
    setActiveTab(e.index);
  };

  return (
    <StyledContainer className="bg-white">
      <div className="flex flex-column p-4 pb-2">
        <p className="font-semibold text-lg">Top CPT Code Spread</p>
        <p className="text-sm text-gray-new-600 font-normal mt-2">
          Compare payer rates to strengthen your future contract negotiations.
        </p>
        <div className="flex gap-3 lg:flex-row flex-column mt-3 align-items-center">
          <StyledDropdown
            value={leftSideToCompare}
            optionLabel="label"
            className="p-column-filter w-full md:w-16rem"
            placeholder="Select Price Type"
            options={filteredOptionsToCompareRates}
            onChange={(e) => {
              setLeftSideToCompare(e.value);
            }}
          />
          <p className="text-sm font-semibold text-gray-new-600">Compared to</p>
          <StyledDropdown
            value={rightSideToCompare}
            optionLabel="label"
            className="p-column-filter w-full md:w-16rem"
            placeholder="Select Price Type"
            options={filteredOptionsToCompareRates}
            onChange={(e) => {
              setRightSideToCompare(e.value);
            }}
          />
        </div>
        <div className="flex lg:flex-row flex-column gap-3 mt-3 align-items-center">
          <StyledCompareRateTag>
            {leftSideData?.icon && (
              <leftSideData.icon className="text-gray-new-600 mb-2" />
            )}
            <p className="text-primary text-xl font-semibold mb-2">
              {leftSideData?.rate}
            </p>
            <p className="text-gray-new-900 text-sm mb-1">
              {leftSideData?.label}
              {leftSideData?.showCoverage && (
                <span className="text-gray-500 text-xs ml-1">
                  {leftSideData.coverageText}
                </span>
              )}
            </p>
            <div
              style={{ minHeight: "30px" }}
              className="flex flex-wrap gap-1 align-items-center"
            >
              {leftSideVisiblePayers?.length
                ? leftSideVisiblePayers.map((payer: string, index: number) => (
                    <span key={index} className="text-gray-500 text-xs">
                      {payer}
                      {index < leftSideVisiblePayers.length - 1 ? ", " : ""}
                    </span>
                  ))
                : null}
              {!showAllLeftSidePayers && leftSideRemainingPayersCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllLeftSidePayers(true)}
                  className="ml-2 text-gray-600 text-xs font-semibold bg-gray-200 border-circle  p-1 border-none cursor-pointer"
                >
                  +{leftSideRemainingPayersCount}
                </button>
              )}
              {showAllLeftSidePayers &&
                leftSideData?.payers &&
                leftSideData?.payers?.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllLeftSidePayers(false)}
                    className="ml-2 text-gray-600 text-xs font-semibold bg-gray-200 border-circle w-1rem h-1rem border-none cursor-pointer"
                  >
                    ✕
                  </button>
                )}
            </div>
          </StyledCompareRateTag>
          <div
            style={{ minWidth: "88px" }}
            className="flex justify-content-center"
          >
            <i
              className="pi pi-arrow-right-arrow-left text-gray-new-400"
              style={{ fontSize: "24px", color: "#98A2B3" }}
            />
          </div>
          <StyledCompareRateTag>
            {rightSideData?.icon && (
              <rightSideData.icon className="text-primary mb-2" />
            )}
            <div className="flex gap-2 align-items-center mb-2">
              <p className="text-primary text-xl font-semibold">
                {rightSideData?.rate}
              </p>
              {rateToShow ? (
                <p className="text-xs text-red-500">{rateToShow}%</p>
              ) : null}
            </div>
            <p className="text-gray-new-900 text-sm mb-1">
              {rightSideData?.label}
              {rightSideData?.showCoverage && (
                <span className="text-gray-500 text-xs ml-1">
                  {rightSideData.coverageText}
                </span>
              )}
            </p>
            <div
              style={{ minHeight: "30px" }}
              className="flex flex-wrap gap-1 align-items-center"
            >
              {rightSideVisiblePayers?.length
                ? rightSideVisiblePayers.map((payer: string, index: number) => (
                    <span key={index} className="text-gray-500 text-xs">
                      {payer}
                      {index < rightSideVisiblePayers.length - 1 ? ", " : ""}
                    </span>
                  ))
                : null}
              {!showAllRightSidePayers && rightSideRemainingPayersCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllRightSidePayers(true)}
                  className="ml-2 text-gray-600 text-xs font-semibold bg-gray-200 border-circle  p-1 border-none cursor-pointer"
                >
                  +{rightSideRemainingPayersCount}
                </button>
              )}
              {showAllRightSidePayers &&
                rightSideData?.payers &&
                rightSideData?.payers?.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllRightSidePayers(false)}
                    className="ml-2 text-gray-600 text-xs font-semibold bg-gray-200 border-circle w-1rem h-1rem border-none cursor-pointer"
                  >
                    ✕
                  </button>
                )}
            </div>
          </StyledCompareRateTag>
        </div>
      </div>
      <HorizontalLine className="border-gray-new-200 mb-0" />
      <div className="flex flex-column lg:flex-row">
        <div
          className="flex flex-column p-4 pt-4 w-full lg:w-6 p-4 lg:border-right-1"
          style={{ borderColor: "#eaecf0" }}
        >
          <p className="text-md font-bold mb-2">Result summary</p>
          <StyledTabView onTabChange={handleTabChange} activeIndex={activeTab}>
            <TabPanel header="Avg Price Spread">
              <AvgPriceSpread top10Data={top10Data} />
            </TabPanel>
            <TabPanel header="Top Scoring Payers">
              <TopScoringPayers
                dataFromAPI={dataFromAPIAllowableVsPtResponsibility}
              />
            </TabPanel>
            <TabPanel header="Low Performing Payers">
              <LowScoringPayers
                dataFromAPI={dataFromAPIAllowableVsPtResponsibility}
              />
            </TabPanel>
          </StyledTabView>
        </div>
        {activeTab === 0 ? (
          <AIRecommendations
            topResultsAmount={topResultsAmount}
            highestCommercialPayers={highestCommercialPayers}
            lowestCommercialPayers={lowestCommercialPayers}
            highestVsLowestCommercial={
              top10Data?.payerCentricApproachSummary?.variance
                ?.highestVsLowestCommercial || null
            }
            contractInsightsData={contractInsightsData}
            isLoadingContractInsights={isLoadingContractInsights}
          />
        ) : (
          <Metrics />
        )}
      </div>
    </StyledContainer>
  );
}
